<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import pixDetectAPI from 'dashboard/api/pixDetect';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Btn from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const loading = ref(true);
const saving = ref(false);
const config = ref({
  global_detection: true,
  min_value_enabled: false,
  min_value: 0,
  validate_receiver: true,
  meta_event_enabled: true,
  meta_event: 'Purchase',
  meta_value_override: null,
  webhook_url: '',
});
const receivers = ref([]);
const sales = ref([]);
const pixels = ref([]);

// Form recebedor
const recName = ref('');
const recCpf = ref('');
const recKey = ref('');
const recType = ref('Telefone');

// Form pixel
const pxName = ref('');
const pxId = ref('');
const pxPage = ref('');
const pxToken = ref('');

const showToast = useAlert;

const EVENT_OPTIONS = ['Purchase', 'Lead', 'InitiateCheckout', 'ViewContent'].map(v => ({
  value: v,
  label: v,
}));
const RECEIVER_TYPE_OPTIONS = ['Telefone', 'E-mail', 'CPF', 'CNPJ', 'Aleatória'].map(v => ({
  value: v,
  label: v,
}));

const load = async () => {
  loading.value = true;
  try {
    const [c, r, s, p] = await Promise.all([
      pixDetectAPI.getConfig(),
      pixDetectAPI.getReceivers(),
      pixDetectAPI.getSales(),
      pixDetectAPI.getPixels(),
    ]);
    config.value = { ...config.value, ...(c.data || {}) };
    receivers.value = r.data || [];
    sales.value = s.data || [];
    pixels.value = p.data || [];
  } catch (e) {
    showToast(e.response?.data?.error || e.message, 'error');
  } finally {
    loading.value = false;
  }
};

onMounted(load);

const saveConfig = async () => {
  saving.value = true;
  try {
    await pixDetectAPI.saveConfig(config.value);
    showToast('Configuração salva!');
  } catch (e) {
    showToast(e.response?.data?.error || e.message, 'error');
  } finally {
    saving.value = false;
  }
};

const addReceiver = async () => {
  if (!recName.value.trim()) return showToast('Informe o nome do recebedor', 'error');
  try {
    await pixDetectAPI.addReceiver({
      name: recName.value,
      cpf_cnpj: recCpf.value,
      pix_key: recKey.value,
      pix_type: recType.value,
    });
    recName.value = '';
    recCpf.value = '';
    recKey.value = '';
    showToast('Recebedor adicionado!');
    load();
  } catch (e) {
    showToast(e.response?.data?.error || e.message, 'error');
  }
};

const removeReceiver = async id => {
  try {
    await pixDetectAPI.deleteReceiver(id);
    showToast('Recebedor removido');
    load();
  } catch {
    showToast('Erro ao remover', 'error');
  }
};

const addPixel = async () => {
  if (!pxId.value.trim()) return showToast('Informe o Pixel ID', 'error');
  try {
    await pixDetectAPI.addPixel({
      name: pxName.value || 'Pixel Principal',
      pixel_id: pxId.value,
      page_id: pxPage.value,
      token: pxToken.value,
    });
    pxId.value = '';
    pxPage.value = '';
    pxToken.value = '';
    showToast('Pixel adicionado!');
    load();
  } catch (e) {
    showToast(e.response?.data?.error || e.message, 'error');
  }
};

const removePixel = async id => {
  try {
    await pixDetectAPI.deletePixel(id);
    showToast('Pixel removido');
    load();
  } catch {
    showToast('Erro ao remover pixel', 'error');
  }
};

const setSaleStatus = async (id, status) => {
  try {
    await pixDetectAPI.updateSaleStatus(id, status);
    load();
  } catch {
    showToast('Erro ao atualizar venda', 'error');
  }
};

const deleteSale = async id => {
  // eslint-disable-next-line no-alert
  if (!window.confirm('Tem certeza que deseja apagar esta venda permanentemente?')) return;
  try {
    await pixDetectAPI.deleteSale(id);
    sales.value = sales.value.filter(s => s.id !== id);
  } catch (e) {
    showToast(e.response?.data?.error || 'Erro ao apagar venda', 'error');
  }
};

const fmtMoney = v => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = d => {
  try {
    return new Date(d).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d;
  }
};

const totals = computed(() => ({
  total: sales.value.length,
  confirmed: sales.value.filter(s => s.status === 'confirmado').length,
  faturamento: sales.value
    .filter(s => s.status === 'confirmado')
    .reduce((a, s) => a + Number(s.amount || 0), 0),
  metaSent: sales.value.filter(s => s.meta_sent).length,
}));
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-solid-1">
    <div class="mx-auto max-w-6xl px-6 py-6 flex flex-col gap-6">
      <!-- Header -->
      <div class="flex items-center gap-3">
        <div class="flex items-center justify-center size-10 rounded-xl bg-n-brand text-white">
          <Icon name="scan-line" :size="20" />
        </div>
        <div>
          <h1 class="text-heading-1 text-n-slate-12">PIX Detect</h1>
          <p class="text-description text-n-slate-11">
            Detecta automaticamente comprovantes PIX em imagens e PDFs e ativa gatilhos de venda.
          </p>
        </div>
      </div>

      <!-- Stats -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <CardLayout layout="row" class="p-4">
          <div class="flex flex-col gap-1">
            <p class="text-caption text-n-slate-11">Vendas Detectadas</p>
            <p class="text-display text-n-slate-12">{{ totals.total }}</p>
            <p class="text-caption text-n-slate-11">{{ totals.metaSent }} com Purchase enviado</p>
          </div>
        </CardLayout>
        <CardLayout layout="row" class="p-4">
          <div class="flex flex-col gap-1">
            <p class="text-caption text-n-slate-11">Confirmadas</p>
            <p class="text-display text-n-slate-12">{{ totals.confirmed }}</p>
            <p class="text-caption text-n-slate-11">Vendas confirmadas</p>
          </div>
        </CardLayout>
        <CardLayout layout="row" class="p-4">
          <div class="flex flex-col gap-1">
            <p class="text-caption text-n-slate-11">Faturamento</p>
            <p class="text-display text-n-slate-12">{{ fmtMoney(totals.faturamento) }}</p>
            <p class="text-caption text-n-slate-11">Vendas confirmadas</p>
          </div>
        </CardLayout>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-20">
        <Spinner />
      </div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <!-- Coluna esquerda -->
        <div class="flex flex-col gap-6">
          <!-- Detecção global -->
          <CardLayout class="p-5">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-heading-4 text-n-slate-12">Detecção Global de Pagamentos</h3>
              <Switch v-model="config.global_detection" />
            </div>
            <p class="text-description text-n-slate-11">
              Reconhece comprovantes PIX em qualquer imagem/PDF recebido. O sistema detecta o comprovante e registra a venda.
            </p>
          </CardLayout>

          <!-- Recebedores -->
          <CardLayout class="p-5">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-heading-4 text-n-slate-12">Validar destinatário do comprovante</h3>
              <Switch v-model="config.validate_receiver" />
            </div>
            <p class="text-description text-n-slate-11 mb-4">
              Valida se o destinatário está entre os recebedores cadastrados.
            </p>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input v-model="recName" label="Nome do Recebedor" placeholder="Ex: João da Silva" />
              <Input v-model="recCpf" label="CPF / CNPJ" placeholder="CPF ou CNPJ do recebedor" />
              <Input v-model="recKey" label="Chave Pix (opcional)" placeholder="Telefone, e-mail ou chave aleatória" />
              <div>
                <span class="text-caption text-n-slate-11 mb-1 block">Tipo da chave</span>
                <Select v-model="recType" :options="RECEIVER_TYPE_OPTIONS" />
              </div>
            </div>

            <div class="mt-4">
              <Btn label="Adicionar recebedor" color="blue" @click="addReceiver">
                <template #prefix>
                  <Icon name="plus" :size="16" />
                </template>
              </Btn>
            </div>

            <div v-if="receivers.length" class="mt-4 flex flex-col gap-2">
              <div
                v-for="r in receivers"
                :key="r.id"
                class="flex items-center gap-3 p-3 rounded-lg bg-n-solid-2 outline outline-1 outline-n-container"
              >
                <Avatar :name="r.name" size="sm" />
                <div class="flex-1 min-w-0">
                  <p class="text-sm text-n-slate-12 font-medium">{{ r.name }}</p>
                  <p class="text-xs text-n-slate-11 truncate">
                    {{ [r.cpf_cnpj, r.pix_key, r.pix_type].filter(Boolean).join(' · ') }}
                  </p>
                </div>
                <Btn variant="ghost" color="ruby" icon="trash-2" @click="removeReceiver(r.id)" />
              </div>
            </div>
          </CardLayout>

          <!-- Meta evento -->
          <CardLayout class="p-5">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-heading-4 text-n-slate-12">Envio Automático de Evento para Meta</h3>
              <Switch v-model="config.meta_event_enabled" />
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <span class="text-caption text-n-slate-11 mb-1 block">Evento</span>
                <Select v-model="config.meta_event" :options="EVENT_OPTIONS" />
              </div>
              <Input
                v-model="config.meta_value_override"
                label="Valor fixo (vazio = valor do PIX)"
                placeholder="Vazio = valor do PIX"
                type="number"
              />
            </div>

            <div class="mt-3">
              <Input
                v-model="config.webhook_url"
                label="Webhook de eventos (opcional — sobrescreve o Pixel Meta)"
                placeholder="https://n8n2.blackflow.qzz.io/webhook/crlifestyle/evo-final"
              />
              <p class="text-caption text-n-slate-11 mt-1">
                Se preenchido, o evento é enviado para este webhook (n8n/externo). Se vazio, usa a Conversions API da Meta.
              </p>
            </div>
          </CardLayout>

          <!-- Vendas -->
          <CardLayout class="p-5">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-heading-4 text-n-slate-12">Vendas registradas</h3>
              <Btn variant="ghost" icon="refresh" @click="load" />
            </div>

            <p
              v-if="!sales.length"
              class="text-description text-n-slate-11 text-center py-8"
            >
              Nenhuma venda detectada ainda. Envie um comprovante PIX para testar.
            </p>

            <div v-else class="flex flex-col gap-2 max-h-96 overflow-y-auto">
              <div
                v-for="s in sales"
                :key="s.id"
                class="p-3 rounded-lg bg-n-solid-2 outline outline-1 outline-n-container"
              >
                <div class="flex items-center gap-2">
                  <strong class="text-sm text-n-slate-12">{{ fmtMoney(s.amount) }}</strong>
                  <span v-if="s.meta_sent" class="text-xs text-n-brand bg-n-brand-faded px-2 py-0.5 rounded-full">
                    Purchase ✓
                  </span>
                  <span class="text-xs text-n-slate-11">{{ fmtDate(s.created_at) }}</span>
                </div>
                <p class="text-xs text-n-slate-11 mt-1 truncate">
                  {{ s.contact_name || s.jid || '—' }}
                  <template v-if="s.matched_receiver">→ {{ s.matched_receiver }}</template>
                </p>
                <div class="flex items-center gap-2 mt-2">
                  <span
                    class="text-xs px-2 py-0.5 rounded-full"
                    :class="
                      s.status === 'confirmado'
                        ? 'text-n-teal bg-n-teal-faded'
                        : 'text-n-amber bg-n-amber-faded'
                    "
                  >
                    {{ s.status === 'confirmado' ? 'Confirmado' : 'Pendente' }}
                  </span>
                  <Btn
                    variant="ghost"
                    color="slate"
                    :label="s.status === 'confirmado' ? 'Descartar' : 'Confirmar'"
                    @click="setSaleStatus(s.id, s.status === 'confirmado' ? 'descartado' : 'confirmado')"
                  />
                  <Btn
                    variant="ghost"
                    color="ruby"
                    label="Apagar"
                    icon="trash-2"
                    @click="deleteSale(s.id)"
                  />
                </div>
              </div>
            </div>
          </CardLayout>
        </div>

        <!-- Coluna direita -->
        <div class="flex flex-col gap-6">
          <!-- Pixels Meta -->
          <CardLayout class="p-5">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-heading-4 text-n-slate-12">Pixels Meta</h3>
              <Btn variant="ghost" icon="refresh" @click="load" />
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input v-model="pxName" label="Nome (ex: Pixel Principal)" placeholder="Pixel Principal" />
              <Input v-model="pxId" label="Pixel ID" placeholder="Ex: 1234567890123456" />
              <Input v-model="pxPage" label="Page ID (opcional)" placeholder="Ex: 987654321012345" />
              <Input v-model="pxToken" label="Token de acesso" placeholder="EAAG..." type="password" />
            </div>

            <div class="mt-4">
              <Btn label="Adicionar Pixel" color="blue" @click="addPixel">
                <template #prefix>
                  <Icon name="plus" :size="16" />
                </template>
              </Btn>
            </div>

            <div v-if="pixels.length" class="mt-4 flex flex-col gap-2">
              <div
                v-for="p in pixels"
                :key="p.id"
                class="p-3 rounded-lg bg-n-solid-2 outline outline-1 outline-n-container"
              >
                <div class="flex items-center gap-2">
                  <strong class="flex-1 text-sm text-n-slate-12">{{ p.name }}</strong>
                  <span class="text-xs text-n-teal">Ativo</span>
                  <Btn variant="ghost" color="ruby" icon="trash-2" @click="removePixel(p.id)" />
                </div>
                <p class="text-xs text-n-slate-11 mt-1">
                  ID: {{ p.pixel_id }}{{ p.page_id ? ` · Page: ${p.page_id}` : '' }}
                </p>
              </div>
            </div>
          </CardLayout>

          <!-- Salvar -->
          <CardLayout class="p-5">
            <h3 class="text-heading-4 text-n-slate-12 mb-4">Salvar configurações</h3>
            <Btn
              class="w-full"
              label="Salvar Configurações"
              color="blue"
              :is-loading="saving"
              @click="saveConfig"
            />
          </CardLayout>
        </div>
      </div>
    </div>
  </div>
</template>